import React, {useState, useEffect} from 'react';
import {Box, Flex, Center, Spinner, Divider, useTheme, VStack} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {H1, H2, TextG10, TextG14, TextG16} from '../../components/typography';
import dayjs from 'dayjs';
import {startCase} from 'lodash';
import BackButton from '../../components/BackButton';
import {BlueButton, PinkButton} from '../../components/button';
import ReferralsList, {MembershipReferral} from '../../components/ReferralsList';
import {
  fetchUserReferrals,
  fetchUserPromoCode,
  cancelSubscription,
  resumeSubscription,
} from '../../endpoints/api';
import {QRCodeSVG} from 'qrcode.react';
import {ToastMessage} from '../../components/toast';
import CancelMembershipModal from '../../components/Modals/CancelMembershipModal';
import {addExternalParam} from '../../utils/externalLink';
import {logSubscriptionCancel, logShareEvent, logSubscriptionResume} from '../../utils/analytics';
import useTrackEvent from '../../hooks/useTrackEvent';
import {useBannerHeight} from '../../hooks/useTopBarOffset';
import MembershipPause, {PauseSource} from '../MembershipPause';

const STRIPE_SETTINGS_URL =
  process.env.REACT_APP_STRIPE_SETTINGS_URL ||
  'https://billing.stripe.com/p/login/7sI6qU8mQgxPf8A8ww';

const MembershipDetails = () => {
  const {user, reFetchUserData} = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const {addUserEvent} = useTrackEvent();
  const [referrals, setReferrals] = useState<MembershipReferral[]>([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(true);
  const [promoCode, setPromoCode] = useState<string>('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [pauseOverlay, setPauseOverlay] = useState<{open: boolean; source: PauseSource}>({
    open: false,
    source: 'pause_button',
  });

  const membershipReferLink =
    location.origin + '/gift?p=' + promoCode + '&invite-code=' + user?.referralCode;
  const bannerHeight = useBannerHeight();

  useEffect(() => {
    const loadReferrals = async () => {
      try {
        const response = await fetchUserReferrals();
        if (response?.data?.membership_referrals) {
          setReferrals(response.data.membership_referrals);
        }
      } catch (error) {
        console.error('Error fetching referrals:', error);
      } finally {
        setIsLoadingReferrals(false);
      }
    };

    // Fetch promo code for membership referral
    const fetchPromoCode = async () => {
      try {
        const response = await fetchUserPromoCode();
        if (response && response.data && response.data.pc) {
          setPromoCode(response.data.pc);
        }
      } catch (error) {
        console.error('Error fetching promo code:', error);
      }
    };

    loadReferrals();
    fetchPromoCode();
  }, []);

  const copyReferralLink = (link: string) => {
    navigator.clipboard.writeText(link);
    ToastMessage({
      status: 'success',
      text: 'Link copied to clipboard',
    });

    logShareEvent('membership_referral_link', 'copy_link', user?.referralCode || 'unknown');
  };

  if (!user) {
    return (
      <Center style={{minHeight: '100vh'}}>
        <Spinner thickness="4px" speed="0.65s" emptyColor="lightGrey" color="lightBlue" size="lg" />
      </Center>
    );
  }

  // Find current membership from user's active memberships
  const activeMembership = user?.activeMemberships?.[0];

  // Find membership plan details from user's membership plans
  const membershipPlan = user?.activeMembershipPlan;

  // Find membership plan details from user's membership subscription
  const activeMembershipPlanSubscription = user?.activeMembershipPlanSubscription;

  // Get subscription status text
  const getStatusText = () => {
    if (!activeMembershipPlanSubscription) return 'Inactive';
    return startCase(activeMembershipPlanSubscription.stripe_status);
  };
  // Format the paid through date
  const getPaidThroughDate = () => {
    if (activeMembershipPlanSubscription && activeMembershipPlanSubscription?.paid_through_date) {
      return dayjs(activeMembershipPlanSubscription.paid_through_date).format('MMMM D, YYYY');
    }
    return 'N/A';
  };

  const handleManageMembership = () => {
    if (user && user.email) {
      window.open(
        addExternalParam(
          `${STRIPE_SETTINGS_URL}?prefilled_email=${encodeURIComponent(user.email)}`,
        ),
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      window.open(`${STRIPE_SETTINGS_URL}`, '_blank', 'noopener,noreferrer');
    }
  };

  const isPaused = activeMembershipPlanSubscription?.stripe_status === 'paused';
  const pauseEndsDate = activeMembershipPlanSubscription?.resume_on
    ? dayjs(activeMembershipPlanSubscription.resume_on).format('MMMM D, YYYY')
    : null;
  const canShowPauseCancelButtons =
    activeMembershipPlanSubscription &&
    !isPaused &&
    !activeMembershipPlanSubscription?.cancel_at &&
    !activeMembershipPlanSubscription?.canceled_at;

  const handleResumeMembership = async () => {
    setIsResuming(true);
    try {
      await resumeSubscription();
      logSubscriptionResume(activeMembershipPlanSubscription);
      await reFetchUserData();
      ToastMessage({status: 'success', text: 'Your membership has been resumed.'});
    } catch (err: any) {
      ToastMessage({
        status: 'error',
        text: err?.message || 'Failed to resume membership',
      });
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <Box p={4} pt={`${16 + bannerHeight}px`} pb="180px" maxW="800px" mx="auto">
      {/* Header with integrated Back Button */}
      <Box p={5} borderRadius="lg" mb={6} background={theme.colors.blue} boxShadow="md">
        <Flex alignItems="center">
          <Box width="40px">
            <BackButton onClick={() => navigate(-1)} sticky={false} />
          </Box>
          <Flex flex="1" justifyContent="center">
            <Box textAlign="center">
              <H1 mt={0} mb={1} color="white">
                {membershipPlan?.name || 'Membership Details'}
              </H1>
              <TextG16 fontWeight="medium" color="white">
                {getStatusText()} Membership
              </TextG16>
            </Box>
          </Flex>
          <Box width="40px"></Box>
        </Flex>
      </Box>

      <Box p={5} borderRadius="lg" borderWidth="1px" mb={6} bg="white">
        <H2 mt={0} mb={4}>
          Membership Information
        </H2>

        <Flex direction="column" gap={4}>
          <Flex justify="space-between" align="center">
            <TextG16 fontWeight="medium">Membership Status:</TextG16>
            <TextG16 color={isPaused ? '#D69E2E' : 'green.500'}>{getStatusText()}</TextG16>
          </Flex>

          {
            <Flex justify="space-between" align="center">
              <TextG16 fontWeight="medium">Auto-Renewal:</TextG16>
              <TextG16>
                {isPaused
                  ? 'Paused'
                  : !activeMembershipPlanSubscription ||
                      activeMembershipPlanSubscription?.cancel_at ||
                      activeMembershipPlanSubscription?.canceled_at
                    ? 'Disabled'
                    : 'Enabled'}
              </TextG16>
            </Flex>
          }

          {isPaused && pauseEndsDate && (
            <Flex justify="space-between" align="center">
              <TextG16 fontWeight="medium">Pause Ends:</TextG16>
              <TextG16>{pauseEndsDate}</TextG16>
            </Flex>
          )}

          <Flex justify="space-between" align="center">
            <TextG16 fontWeight="medium">Paid Through:</TextG16>
            <TextG16>{getPaidThroughDate()}</TextG16>
          </Flex>

          <Flex justify="space-between" align="center">
            <TextG16 fontWeight="medium">Membership Type:</TextG16>
            <TextG16>{membershipPlan?.name || 'Premium'}</TextG16>
          </Flex>

          <Flex justify="space-between" align="center">
            <TextG16 fontWeight="medium">Monthly Cost:</TextG16>
            <TextG16>${membershipPlan?.monthly_cost || 'NA'}</TextG16>
          </Flex>

          {activeMembershipPlanSubscription?.created_at && (
            <Flex justify="space-between" align="center">
              <TextG16 fontWeight="medium">Membership Started:</TextG16>
              <TextG16>
                {dayjs(activeMembershipPlanSubscription.created_at).format('MMMM D, YYYY')}
              </TextG16>
            </Flex>
          )}
        </Flex>

        {/* <Divider my={4} />

        <VStack spacing={3} width="100%">
          <BlueButton onClick={handleManageMembership} size="md" width="100%">
            Manage Membership in Stripe
          </BlueButton>
        </VStack> */}
      </Box>

      {/* <Box p={5} borderRadius="lg" borderWidth="1px" mb={6} bg="white">
        <H2 mt={0} mb={4}>
          Give a Month, Get a Month!
        </H2>

        <TextG14 mb={4} color="gray.600">
          When friends join Walkabout and sign-up for Local Insiders Club with your invite, you both
          get a free month of membership!
        </TextG14>

        <Box>
          <TextG14 textAlign="center" fontWeight="medium" fontSize="md" mt={6}>
            Have your friend scan your membership referral QR code
          </TextG14>
        </Box>

        <Flex justify="center" width="100%" my={4}>
          <QRCodeSVG value={membershipReferLink} size={180} />
        </Flex>

        <Box width="100%">
          <Box my={6}>
            <TextG14 fontWeight="medium" textAlign="center" fontSize="md">
              OR
            </TextG14>
          </Box>

          <TextG14 textAlign="center" fontWeight="medium" fontSize="md" mb={2}>
            Copy your membership referral link:
          </TextG14>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            pl="16px"
            pr="4px"
            py="4px"
            border="1px solid #E2E8F0"
            borderRadius="10px"
            mb={4}>
            <TextG10 mr="8px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
              {membershipReferLink}
            </TextG10>
            <BlueButton
              onClick={() => copyReferralLink(membershipReferLink)}
              fontSize="10px"
              maxH="40px"
              borderRadius="10px">
              Copy Link
            </BlueButton>
          </Flex>
        </Box>

        <Divider my={4} />

        <ReferralsList referrals={referrals} isLoading={isLoadingReferrals} />
      </Box> */}

      {(canShowPauseCancelButtons || isPaused) && (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          bg="white"
          px={5}
          pt={3}
          pb={9}
          display="flex"
          flexDirection="column"
          gap={3}
          alignItems="center"
          boxShadow="0 -4px 20px rgba(0,0,0,0.06) !important"
          zIndex={5}>
          {canShowPauseCancelButtons && (
            <Box
              as="button"
              onClick={() => {
                addUserEvent({
                  type: 'click',
                  event_name: 'cancel_membership_clicked',
                  data: {source: 'membership_details'},
                });
                setPauseOverlay({open: true, source: 'cancel_button'});
              }}
              bg="transparent"
              border="none"
              cursor="pointer"
              color="#262626"
              fontWeight={500}
              lineHeight="24px"
              textDecoration="underline"
              textUnderlineOffset="auto"
              p={1}>
              <TextG16>Cancel Membership</TextG16>
            </Box>
          )}

          {isPaused && (
            <PinkButton
              onClick={handleResumeMembership}
              h="52px"
              borderRadius="100px"
              width="100%"
              maxW="400px"
              isLoading={isResuming}>
              Resume Membership
            </PinkButton>
          )}
        </Box>
      )}

      <MembershipPause
        isOpen={pauseOverlay.open}
        source={pauseOverlay.source}
        onClose={() => setPauseOverlay((s) => ({...s, open: false}))}
      />

      <CancelMembershipModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        membershipPlanName={membershipPlan?.name || 'Premium'}
        paidThroughDate={activeMembershipPlanSubscription?.paid_through_date}
        isLoading={isCancelling}
        onConfirm={(reasonId, reasonComment) => {
          setIsCancelling(true);
          cancelSubscription({reasonId, reasonComment})
            .then(() => {
              logSubscriptionCancel(activeMembershipPlanSubscription);
              ToastMessage({
                duration: 5000,
                status: 'success',
                text:
                  activeMembershipPlanSubscription &&
                  activeMembershipPlanSubscription?.paid_through_date
                    ? 'Your membership has been cancelled. Your credit card will not be charged. You still have access to your paid membership until ' +
                      dayjs(activeMembershipPlanSubscription?.paid_through_date).format(
                        'MMMM D, YYYY',
                      ) +
                      '.'
                    : 'Your membership has been cancelled. Your credit card will not be charged. You still have access to your free account.',
              });
              reFetchUserData();
              setCancelModalOpen(false);
            })
            .catch((err) => {
              ToastMessage({
                status: 'error',
                text: err.message || 'Failed to cancel membership',
              });
            })
            .finally(() => {
              setIsCancelling(false);
            });
        }}
      />
    </Box>
  );
};

export default MembershipDetails;
